import { Link } from "react-router-dom";
import { Users, BedDouble, Bath, ArrowRight, Wifi, Car, Phone, PawPrint, Baby, Bus, Wind } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { ROOM_IMAGES } from "@/data/site";
import { Reveal, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";

const SHARED_ICONS = [Wifi, Wind, Car, Baby, Bus];

export default function Rooms() {
  const { t, lang } = useLang();
  const rp = t.roomsPage;

  return (
    <div data-testid="rooms-page" key={lang} className="pt-16 sm:pt-20">
      <Seo title={t.seo.rooms.title} description={t.seo.rooms.desc} image="/images/room-double.jpg" />

      <header className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-14 sm:pt-20 pb-10">
        <Reveal>
          <Eyebrow>Sobe Bevanda — Međugorje</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F2421]">{rp.title}</h1>
          <p className="text-lg sm:text-xl text-[#4A504B] font-light leading-relaxed mt-6 max-w-2xl">{rp.lead}</p>
        </Reveal>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-8">
        {rp.items.map((room, i) => (
          <Reveal key={room.id} delay={0.05}>
            <article
              data-testid={`room-detail-${room.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center py-12 sm:py-16 border-t border-[#1F2421]/10 ${i % 2 === 1 ? "" : ""}`}
            >
              <div className={`overflow-hidden rounded-2xl ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={ROOM_IMAGES[room.id]}
                  alt={room.name}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover hover:scale-[1.03] transition-transform duration-700"
                />
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <span className="font-serif-display italic text-4xl text-[#4A5D3E]/25">0{i + 1}</span>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1F2421] mt-2">{room.name}</h2>
                <p className="text-[#4A504B] leading-relaxed mt-4 max-w-lg">{room.desc}</p>

                <dl className="mt-7 space-y-3.5 text-sm sm:text-base">
                  <div className="flex items-center gap-3">
                    <Users size={17} className="text-[#4A5D3E] shrink-0" />
                    <dt className="text-[#7A8278] w-36 shrink-0">{rp.capacity}</dt>
                    <dd className="text-[#1F2421] font-medium">
                      {room.capacity} {Number(room.capacity) === 1 ? rp.guest : rp.guests}
                    </dd>
                  </div>
                  <div className="flex items-center gap-3">
                    <BedDouble size={17} className="text-[#4A5D3E] shrink-0" />
                    <dt className="text-[#7A8278] w-36 shrink-0">{rp.beds}</dt>
                    <dd className="text-[#7A8278] italic">{rp.onRequest}</dd>
                  </div>
                  <div className="flex items-center gap-3">
                    <Bath size={17} className="text-[#4A5D3E] shrink-0" />
                    <dt className="text-[#7A8278] w-36 shrink-0">{rp.bathroom}</dt>
                    <dd className="text-[#7A8278] italic">{rp.onRequest}</dd>
                  </div>
                </dl>

                <Link
                  to={`/kontakt?room=${room.id}`}
                  data-testid={`room-inquire-${room.id}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#4A5D3E] hover:bg-[#3A4A31] text-[#F9F8F3] text-sm font-semibold px-6 py-3 mt-8 transition-colors duration-300"
                >
                  {rp.inquire}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <section data-testid="rooms-shared-amenities" className="bg-[#F3EFE6] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="font-serif-display text-2xl sm:text-3xl text-[#1F2421]">{rp.sharedTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5 mt-8">
            {rp.shared.map((a, i) => {
              const Icon = SHARED_ICONS[i] || Wifi;
              return (
                <Reveal key={a} delay={i * 0.05} className="flex items-center gap-3">
                  <Icon size={17} className="text-[#4A5D3E] shrink-0" />
                  <span className="text-sm sm:text-base text-[#1F2421]">{a}</span>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <p className="text-sm text-[#7A8278] italic mt-10 max-w-2xl">{rp.note}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
